/**
 * Home Page Logic - Puzzle Paradise
 * A warm and cozy puzzle selection experience
 */

import { AchievementSystem } from './core/AchievementSystem.js';
import { AchievementModal } from './ui/AchievementModal.js';
import { apiService } from './core/ApiService.js';
import { AuthModal } from './ui/AuthModal.js';
import { LeaderboardModal } from './ui/LeaderboardModal.js';
import { ProfileModal } from './ui/ProfileModal.js';
import { toast } from './ui/Toast.js';
import { i18n } from './utils/I18n.js';

class HomePage {
    constructor() {
        // Game settings
        this.selectedDifficulty = { cols: 4, rows: 4 };
        this.currentCategory = 'story1';
        this.customImageData = null;
        
        // Pagination
        this.currentPage = 1;
        this.itemsPerPage = 12;

        // User Stats
        this.completedPuzzles = [];

        // Puzzle metadata with categories
        this.puzzleData = this.initPuzzleData();

        // Systems
        this.achievementSystem = new AchievementSystem();
        this.achievementModal = new AchievementModal(this.achievementSystem);
        
        this.authModal = new AuthModal((user) => this.onLoginSuccess(user));
        this.leaderboardModal = new LeaderboardModal();
        this.profileModal = new ProfileModal((user) => this.onProfileUpdate(user));

        this._injectDropdownStyles();
        this.init();
    }
    
    _injectDropdownStyles() {
        if (document.getElementById('dropdown-styles')) return;
        const style = document.createElement('style');
        style.id = 'dropdown-styles';
        style.textContent = `
            .user-dropdown-container { position: relative; }
            .user-avatar-small { width: 24px; height: 24px; border-radius: 50%; margin-right: 8px; object-fit: cover; }
            
            .user-dropdown-menu {
                position: absolute; top: 110%; right: 0;
                background: white; border-radius: 12px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                padding: 8px 0; min-width: 180px;
                display: none; z-index: 1000;
                transform-origin: top right;
                animation: scaleIn 0.2s ease;
            }
            .user-dropdown-menu.show { display: block; }
            
            .dropdown-item {
                padding: 10px 20px; cursor: pointer; display: flex; align-items: center; gap: 10px;
                color: #5D4E42; font-weight: 500; font-size: 0.95rem;
                transition: background 0.2s;
            }
            .dropdown-item:hover { background: #FDF6EC; color: #A67C52; }
            
            /* Hide manual difficulty selector as we now use adaptive difficulty curve */
            .difficulty-selector { display: none !important; }
            
            @keyframes scaleIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Get difficulty configuration based on index (Difficulty Curve)
     * Curve: Rise -> Dip -> Rise Higher -> Dip -> Rise Highest
     */
    getDifficultyByIndex(index) {
        const levels = [
            { cols: 4, rows: 4 },   // 0: 16 pcs (Start)
            { cols: 5, rows: 5 },   // 1: 25 pcs
            { cols: 6, rows: 6 },   // 2: 36 pcs
            { cols: 7, rows: 7 },   // 3: 49 pcs
            { cols: 5, rows: 5 },   // 4: 25 pcs (Dip - Relax)
            { cols: 8, rows: 8 },   // 5: 64 pcs
            { cols: 9, rows: 9 },   // 6: 81 pcs
            { cols: 10, rows: 10 }, // 7: 100 pcs
            { cols: 7, rows: 7 },   // 8: 49 pcs (Dip - Relax)
            { cols: 11, rows: 11 }, // 9: 121 pcs
            { cols: 12, rows: 12 }, // 10: 144 pcs
            { cols: 13, rows: 13 }, // 11: 169 pcs
            { cols: 10, rows: 10 }, // 12: 100 pcs (Dip)
            { cols: 14, rows: 14 }, // 13: 196 pcs
            { cols: 15, rows: 15 }, // 14: 225 pcs
            { cols: 16, rows: 16 }, // 15: 256 pcs (The New Peak)
            { cols: 12, rows: 12 }, // 16: 144 pcs (Relax)
        ];

        // For indices beyond the defined levels, use the cycle
        if (index >= levels.length) {
            // Cycle through the high difficulty part (from 100+ pieces)
            const cycleStart = 11;
            const cycleIndex = cycleStart + ((index - levels.length) % (levels.length - cycleStart));
            return levels[cycleIndex];
        }

        return levels[index];
    }

    /**
     * Initialize puzzle metadata with categories
     */
    initPuzzleData() {
        return {
            'p1': { title: 'Sunset Dreams', category: 'nature' },
            'p2': { title: 'Mountain Vista', category: 'nature' },
            'p3': { title: 'Ocean Waves', category: 'nature' },
            'p4': { title: 'Forest Path', category: 'nature' },
            'p5': { title: 'Wild Safari', category: 'animals' },
            'p6': { title: 'Cute Pets', category: 'animals' },
            'p7': { title: 'Bird Paradise', category: 'animals' },
            'p8': { title: 'Under the Sea', category: 'animals' },
            'p9': { title: 'Abstract Art', category: 'art' },
            'p10': { title: 'Classic Painting', category: 'art' },
            'p11': { title: 'Modern Design', category: 'art' },
            'p12': { title: 'Color Splash', category: 'art' },
            'p13': { title: 'City Lights', category: 'places' },
            'p14': { title: 'Ancient Temple', category: 'places' },
            'p15': { title: 'Cozy Village', category: 'places' },
            'p16': { title: 'Beach Resort', category: 'places' },
            'p17': { title: 'Spring Garden', category: 'nature' },
            'p18': { title: 'Winter Wonder', category: 'nature' },
            'p19': { title: 'Butterfly Dance', category: 'animals' },
            'p20': { title: 'Street Scene', category: 'places' },
            'p21': { title: 'Floral Pattern', category: 'art' },
            'p22': { title: 'Golden Hour', category: 'nature' }
        };
    }

    async init() {
        // Handle OAuth callback if present in URL hash (must be before i18n.init)
        const oauthResult = apiService.handleOAuthCallback();

        await i18n.init(); // 初始化多语言

        if (oauthResult && oauthResult.error) {
            setTimeout(() => toast.error(oauthResult.error), 100);
        }
        if (oauthResult && oauthResult.user) {
            setTimeout(() => toast.success(`Welcome, ${oauthResult.user.username}!`), 100);
        }

        if (apiService.isLoggedIn()) {
            try {
                this.completedPuzzles = await apiService.getCompletedPuzzles();
            } catch (e) {
                console.error('Failed to fetch completed puzzles:', e);
            }
        }
        this.renderGallery();
        this.initCarousel();
        this.bindEvents();
        this.updateLoginStatus();
        
        // Global click listener for dropdown closing
        document.addEventListener('click', (e) => {
            const dropdown = document.getElementById('userDropdownMenu');
            const btn = document.getElementById('btnUserMenu');
            if (dropdown && dropdown.classList.contains('show')) {
                if (!btn.contains(e.target) && !dropdown.contains(e.target)) {
                    dropdown.classList.remove('show');
                }
            }
        });
    }

    updateLoginStatus() {
        const headerActions = document.querySelector('.header-actions');
        const user = apiService.getCurrentUser();
        
        // Cleanup existing elements
        const existingDropdown = document.getElementById('userDropdownContainer');
        if (existingDropdown) existingDropdown.remove();
        
        // Cleanup Login Dropdown (if exists from previous version)
        const existingLoginDropdown = document.getElementById('loginDropdownContainer');
        if (existingLoginDropdown) existingLoginDropdown.remove();

        const oldLoginBtn = document.getElementById('btnLogin');
        if (oldLoginBtn) oldLoginBtn.remove();

        if (user) {
            // Logged In: User Dropdown
            const div = document.createElement('div');
            div.id = 'userDropdownContainer';
            div.className = 'user-dropdown-container';
            div.innerHTML = `
                <button class="btn-login user-menu-btn" id="btnUserMenu">
                    <img src="${user.avatar_url || 'https://ui-avatars.com/api/?name=' + user.username}" class="user-avatar-small" referrerpolicy="no-referrer">
                    <span>${user.username}</span>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style="margin-left:4px"><path d="M7 10l5 5 5-5z"/></svg>
                </button>
                <div class="user-dropdown-menu" id="userDropdownMenu">
                    <div class="dropdown-item" id="menuProfile" data-i18n="home.profile">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                        Profile Settings
                    </div>
                    <div class="dropdown-item" id="menuLogout" data-i18n="home.logout">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
                        Logout
                    </div>
                </div>
            `;
            headerActions.appendChild(div);

            div.querySelector('#btnUserMenu').onclick = (e) => {
                e.stopPropagation();
                div.querySelector('#userDropdownMenu').classList.toggle('show');
            };
            div.querySelector('#menuProfile').onclick = () => {
                div.querySelector('#userDropdownMenu').classList.remove('show');
                this.profileModal.show();
            };
            div.querySelector('#menuLogout').onclick = () => {
                div.querySelector('#userDropdownMenu').classList.remove('show');
                if (confirm('Are you sure you want to logout?')) {
                    apiService.logout();
                    this.updateLoginStatus();
                    toast.success('Logged out successfully');
                }
            };

        } else {
            // Not Logged In: Direct Login Button
            const btn = document.createElement('button');
            btn.id = 'btnLogin';
            btn.className = 'btn-login';
            btn.setAttribute('data-i18n', 'home.login');
            btn.innerHTML = `
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                Login
            `;
            btn.onclick = () => this.authModal.show();
            headerActions.appendChild(btn);
        }

        // 重要：动态生成元素后需手动触发一次翻译
        i18n.applyTranslations();
    }

    async onLoginSuccess(user) {
        this.authModal.hide();
        this.updateLoginStatus();
        
        try {
            this.completedPuzzles = await apiService.getCompletedPuzzles();
            this.renderGallery();
        } catch (e) {
            console.error(e);
        }
        
        toast.success(`Welcome back, ${user.username}!`);
    }

    onProfileUpdate(user) {
        this.updateLoginStatus();
        toast.success('Profile updated!');
    }

    /**
     * Initialize Hero Carousel
     */
    initCarousel() {
        const track = document.getElementById('heroCarouselTrack');
        const indicators = document.getElementById('heroCarouselIndicators');
        if (!track || !indicators) return;

        // Dynamically load banner images
        const bannerImages = import.meta.glob('./assets/banner/*.{jpg,JPG,png,PNG}', { eager: true });
        const bannerPaths = Object.keys(bannerImages).sort();

        if (bannerPaths.length === 0) return;

        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const storageKey = 'jigsaw_daily_banners';
        let cached = null;

        try {
            cached = JSON.parse(localStorage.getItem(storageKey));
        } catch (_) { /* ignore */ }

        // Ensure cached is for today and valid
        const isCacheValid = cached && cached.date === today &&
            Array.isArray(cached.paths) &&
            cached.paths.every(p => bannerPaths.includes(p));

        // Pick up to 3 banners
        const maxSlides = 3;
        let selected;

        if (bannerPaths.length <= maxSlides) {
            selected = [...bannerPaths];
        } else if (isCacheValid) {
            selected = cached.paths;
        } else {
            const shuffled = [...bannerPaths];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            selected = shuffled.slice(0, maxSlides);
        }

        // Difficulty options for banner puzzles
        const difficultyOptions = [
            { cols: 4, rows: 4 },   // 16 pcs
            { cols: 5, rows: 5 },   // 25 pcs
            { cols: 6, rows: 6 },   // 36 pcs
            { cols: 7, rows: 7 },   // 49 pcs
            { cols: 8, rows: 8 },   // 64 pcs
            { cols: 9, rows: 9 },   // 81 pcs
        ];

        // Load or generate daily difficulties for each banner
        let difficulties = (isCacheValid && cached.difficulties) ? cached.difficulties : {};
        selected.forEach(path => {
            const fileName = path.split('/').pop().replace(/\.[^/.]+$/, '');
            if (!difficulties[fileName]) {
                const pick = difficultyOptions[Math.floor(Math.random() * difficultyOptions.length)];
                difficulties[fileName] = pick;
            }
        });

        // Save to localStorage (overwrites previous)
        localStorage.setItem(storageKey, JSON.stringify({
            date: today,
            paths: selected,
            difficulties
        }));

        // Create slides with click events
        selected.forEach((path, i) => {
            const imgUrl = bannerImages[path].default || bannerImages[path];
            const fileName = path.split('/').pop().replace(/\.[^/.]+$/, '');
            const difficulty = difficulties[fileName];

            const slide = document.createElement('div');
            slide.className = 'carousel-slide' + (i === 0 ? ' active' : '');
            slide.style.backgroundImage = `url('${imgUrl}')`;
            slide.style.cursor = 'pointer';
            slide.addEventListener('click', () => {
                this.startGame(fileName, false, difficulty);
            });
            track.appendChild(slide);
        });

        // Create indicators (only if more than 1 slide)
        if (selected.length > 1) {
            selected.forEach((_, i) => {
                const dot = document.createElement('button');
                dot.className = 'indicator' + (i === 0 ? ' active' : '');
                dot.dataset.index = i;
                indicators.appendChild(dot);
            });
        }

        const slides = track.querySelectorAll('.carousel-slide');
        const dots = indicators.querySelectorAll('.indicator');

        if (slides.length <= 1) return; // No auto-play for single slide

        let currentIndex = 0;
        const intervalTime = 5000;
        let timer;

        const showSlide = (index) => {
            if (index >= slides.length) index = 0;
            if (index < 0) index = slides.length - 1;
            currentIndex = index;

            slides.forEach((slide, i) => {
                slide.classList.toggle('active', i === index);
            });

            dots.forEach((dot, i) => {
                dot.classList.toggle('active', i === index);
            });
        };

        const nextSlide = () => {
            showSlide(currentIndex + 1);
        };

        const startTimer = () => {
            if (timer) clearInterval(timer);
            timer = setInterval(nextSlide, intervalTime);
        };

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                showSlide(index);
                startTimer();
            });
        });

        startTimer();
    }

    /**
     * Render puzzle gallery with cards
     */
    renderGallery() {
        const gallery = document.getElementById('galleryGrid');
        if (!gallery) return;

        let images = {};
        if (this.currentCategory === 'story1') {
            images = import.meta.glob('./assets/c1/*.{jpg,JPG,png,PNG}', { eager: true });
        } else if (this.currentCategory === 'story2') {
            images = import.meta.glob('./assets/c2/*.{jpg,JPG,png,PNG}', { eager: true });
        } else {
            const jpgImages = import.meta.glob('./assets/picture/*.jpg', { eager: true });
            const jpgImagesUpper = import.meta.glob('./assets/picture/*.JPG', { eager: true });
            images = { ...jpgImages, ...jpgImagesUpper };
        }

        gallery.innerHTML = '';

        // 1. Collect and filter all valid items first
        let filteredItems = [];
        const sortedPaths = Object.keys(images).sort((a, b) => {
            // Sort by the last number in the filename
            const getNum = (str) => {
                const match = str.match(/(\d+)(?=\.[^.]+$)/);
                return match ? parseInt(match[1]) : 0;
            };
            return getNum(a) - getNum(b);
        });

        for (const path of sortedPaths) {
            const fileName = path.split('/').pop().replace(/\.(jpg|JPG|png|PNG)$/i, '');
            const imgUrl = images[path].default || path;

            let puzzleInfo = this.puzzleData[fileName.toLowerCase()];
            
            if (!puzzleInfo) {
                // Generate title from filename
                const title = fileName
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/[_-]/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                
                puzzleInfo = {
                    title: title.charAt(0).toUpperCase() + title.slice(1),
                    category: this.currentCategory
                };
            }

            filteredItems.push({ fileName, imgUrl, puzzleInfo });
        }

        // 2. Pagination Logic
        const totalItems = filteredItems.length;
        const totalPages = Math.ceil(totalItems / this.itemsPerPage);
        
        // Ensure currentPage is valid
        if (this.currentPage > totalPages) this.currentPage = totalPages || 1;
        if (this.currentPage < 1) this.currentPage = 1;

        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        const pageItems = filteredItems.slice(startIndex, endIndex);

        // 3. Render current page items
        pageItems.forEach((item, index) => {
            // Calculate absolute index for difficulty curve
            const absoluteIndex = startIndex + index;
            const difficulty = this.getDifficultyByIndex(absoluteIndex);
            
            const card = this.createPuzzleCard(item.fileName, item.imgUrl, item.puzzleInfo, difficulty);
            gallery.appendChild(card);
        });

        // 4. Render Pagination Controls
        this.renderPagination(totalPages);

        // 5. Apply translations to newly created cards
        i18n.applyTranslations();
    }

    /**
     * Render Pagination Controls
     */
    renderPagination(totalPages) {
        const container = document.getElementById('paginationContainer');
        if (!container) return;
        
        container.innerHTML = '';
        if (totalPages <= 1) return;

        const createBtn = (text, page, isNumber = false, isDisabled = false, isActive = false) => {
            const btn = document.createElement('button');
            btn.className = `pagination-btn ${isNumber ? 'number' : ''} ${isActive ? 'active' : ''}`;
            btn.textContent = text;
            btn.disabled = isDisabled;
            if (!isDisabled && !isActive) {
                btn.onclick = () => {
                    this.currentPage = page;
                    this.renderGallery();
                    // Smooth scroll to top of gallery
                    document.getElementById('galleryGrid').scrollIntoView({ behavior: 'smooth', block: 'start' });
                };
            }
            return btn;
        };

        // Prev Button
        container.appendChild(createBtn('<', this.currentPage - 1, false, this.currentPage === 1));

        // Number Buttons
        // Logic to show a window of pages (e.g. 1 ... 4 5 6 ... 10) can be added here.
        // For simplicity, if pages are few, show all. If many, show limited window.
        
        let startPage = Math.max(1, this.currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);
        
        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }

        if (startPage > 1) {
            container.appendChild(createBtn('1', 1, true));
            if (startPage > 2) {
                const dots = document.createElement('span');
                dots.textContent = '...';
                dots.style.color = 'var(--text-light)';
                container.appendChild(dots);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            container.appendChild(createBtn(i.toString(), i, true, false, i === this.currentPage));
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                const dots = document.createElement('span');
                dots.textContent = '...';
                dots.style.color = 'var(--text-light)';
                container.appendChild(dots);
            }
            container.appendChild(createBtn(totalPages.toString(), totalPages, true));
        }

        // Next Button
        container.appendChild(createBtn('>', this.currentPage + 1, false, this.currentPage === totalPages));
    }

    /**
     * Create a puzzle card element
     */
    createPuzzleCard(id, imgUrl, info, difficulty) {
        const card = document.createElement('div');
        card.className = 'puzzle-card';
        card.dataset.id = id;
        card.dataset.category = info.category;

        // Use the specific difficulty for this card
        const pieceCount = difficulty.cols * difficulty.rows;
        let difficultyClass = 'easy';
        let difficultyText = 'Easy';
        let difficultyKey = 'home.easy';

        if (pieceCount >= 48) {
            difficultyClass = 'hard';
            difficultyText = 'Hard';
            difficultyKey = 'home.hard';
        } else if (pieceCount >= 20) {
            difficultyClass = 'medium';
            difficultyText = 'Medium';
            difficultyKey = 'home.medium';
        }

        const isCompleted = this.completedPuzzles.includes(id.toLowerCase());
        const completedBadge = isCompleted ? `
            <div class="puzzle-completed-badge">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
                Done
            </div>
        ` : '';

        // Added Rank Button
        card.innerHTML = `
            <div class="puzzle-card-image">
                ${completedBadge}
                <img src="${imgUrl}" alt="${info.title}" loading="lazy">
                <div class="puzzle-card-overlay">
                    <button class="btn-start" data-id="${id}" data-i18n="home.start">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                        </svg>
                        Start
                    </button>
                    <button class="btn-rank-card" title="Leaderboard" style="
                        position: absolute; top: 10px; right: 10px;
                        background: rgba(255,255,255,0.9); border:none; border-radius: 50%;
                        width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
                        cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.2); transition: transform 0.2s;
                    ">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="#E07A6B">
                            <path d="M16 11V3H8v6H2v12h20V11h-6zm-6-6h4v14h-4V5zm-6 6h4v8H4v-8zm16 8h-4v-6h4v6z"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div class="puzzle-card-info">
                <div class="puzzle-card-meta">
                    <span class="puzzle-pieces">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7s2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/>
                        </svg>
                        ${pieceCount} <span data-i18n="home.pieces">pieces</span>
                    </span>
                    <span class="puzzle-difficulty ${difficultyClass}" data-i18n="${difficultyKey}">${difficultyText}</span>
                </div>
            </div>
        `;

        const startBtn = card.querySelector('.btn-start');
        startBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.startGame(id, false, difficulty);
        });

        // Rank button click
        const rankBtn = card.querySelector('.btn-rank-card');
        rankBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.leaderboardModal.show(id);
        });

        card.addEventListener('click', () => {
            this.startGame(id, false, difficulty);
        });

        // Add hover effect for rank button
        rankBtn.onmouseover = () => rankBtn.style.transform = 'scale(1.1)';
        rankBtn.onmouseout = () => rankBtn.style.transform = 'scale(1)';

        return card;
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
        document.querySelectorAll('.category-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                this.currentCategory = tab.dataset.category;
                this.currentPage = 1;
                this.renderGallery();
            });
        });

        document.querySelectorAll('.difficulty-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.difficulty-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.selectedDifficulty = {
                    cols: parseInt(btn.dataset.cols),
                    rows: parseInt(btn.dataset.rows)
                };
                this.currentPage = 1;
                this.renderGallery();
            });
        });

        const fileInput = document.getElementById('imageUpload');
        if (fileInput) {
            fileInput.addEventListener('change', (e) => {
                const file = e.target.files[0];
                if (file) {
                    this.handleImageUpload(file);
                }
            });
        }

        const heroUploadCard = document.getElementById('heroUploadCard');
        if (heroUploadCard) {
            heroUploadCard.addEventListener('click', () => {
                document.getElementById('imageUpload').click();
            });
            
            heroUploadCard.addEventListener('dragover', (e) => {
                e.preventDefault();
                heroUploadCard.style.borderColor = 'var(--primary-brown)';
                heroUploadCard.style.background = 'rgba(201, 167, 125, 0.15)';
            });

            heroUploadCard.addEventListener('dragleave', (e) => {
                e.preventDefault();
                heroUploadCard.style.borderColor = '';
                heroUploadCard.style.background = '';
            });

            heroUploadCard.addEventListener('drop', (e) => {
                e.preventDefault();
                heroUploadCard.style.borderColor = '';
                heroUploadCard.style.background = '';

                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('image/')) {
                    this.handleImageUpload(file);
                }
            });
        }

        // Achievement button
        const btnAchievement = document.getElementById('btnAchievement');
        if (btnAchievement) {
            btnAchievement.addEventListener('click', () => {
                this.achievementModal.show();
            });
        }
    }

    async handleImageUpload(file) {
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image too large. Please use images under 10MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const imageData = e.target.result;
            const resizedImageData = await this.resizeImage(imageData, 1024, 1024);
            this.customImageData = resizedImageData;

            toast.success('Image uploaded! Starting game...');
            setTimeout(() => {
                this.startGame(null, true);
            }, 800);
        };
        reader.readAsDataURL(file);
    }

    resizeImage(imageData, containerWidth, containerHeight) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const texWidth = img.width;
                const texHeight = img.height;
                const scaleX = containerWidth / texWidth;
                const scaleY = containerHeight / texHeight;
                const scale = Math.min(scaleX, scaleY, 1);

                const newW = Math.floor(texWidth * scale);
                const newH = Math.floor(texHeight * scale);

                const canvas = document.createElement('canvas');
                canvas.width = newW;
                canvas.height = newH;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newW, newH);

                resolve(canvas.toDataURL('image/png'));
            };
            img.src = imageData;
        });
    }

    startGame(imageId, isCustom = false, difficultyOverride = null) {
        // Use difficulty override if provided, otherwise fallback to selected (though UI is hidden)
        const difficulty = difficultyOverride || this.selectedDifficulty;

        const params = new URLSearchParams({
            cols: difficulty.cols,
            rows: difficulty.rows,
            pieceSize: 80,
            tabSize: 20,
            seed: Math.floor(Math.random() * 10000)
        });

        if (isCustom && this.customImageData) {
            sessionStorage.setItem('customPuzzleImage', this.customImageData);
            params.set('custom', 'true');
        } else if (imageId) {
            params.set('id', imageId);
            sessionStorage.removeItem('customPuzzleImage');
        } else {
            toast.info('Please select an image first.');
            return;
        }

        window.location.href = `game.html?${params.toString()}`;
    }
}

new HomePage();